import React from 'react';
import { Button } from '@clinic-os/ui';
import { cn } from '@clinic-os/ui/lib/cn';

export interface TimeSlot {
  time: string;
  label: string;
}

export interface TimeSlotPickerProps {
  selectedDate: Date;
  selectedTime: string | null;
  onSelect: (timeSlot: string) => void;
  clinicId?: string;
  startHour?: number;
  endHour?: number;
  intervalMinutes?: number;
  className?: string;
}

function generateTimeSlots(
  startHour: number,
  endHour: number,
  intervalMinutes: number
): TimeSlot[] {
  const slots: TimeSlot[] = [];
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const label = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
      slots.push({ time, label });
    }
  }
  return slots;
}

function isSlotDisabled(slot: TimeSlot, selectedDate: Date): boolean {
  const now = new Date();
  const parts = slot.time.split(':').map(Number);
  const hours = parts[0]!;
  const minutes = parts[1]!;
  const slotDate = new Date(selectedDate);
  slotDate.setHours(hours, minutes, 0, 0);

  // Disable past slots
  if (slotDate <= now) {
    return true;
  }

  // Disable weekends (Saturday = 6, Sunday = 0)
  const day = selectedDate.getDay();
  if (day === 0 || day === 6) {
    return true;
  }

  return false;
}

export function TimeSlotPicker({
  selectedDate,
  selectedTime,
  onSelect,
  startHour = 9,
  endHour = 17,
  intervalMinutes = 30,
  className,
}: TimeSlotPickerProps) {
  const slots = generateTimeSlots(startHour, endHour, intervalMinutes);
  const isToday = selectedDate.toDateString() === new Date().toDateString();

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {slots.map((slot) => {
          const disabled = isSlotDisabled(slot, selectedDate);
          const selected = selectedTime === slot.time;

          return (
            <Button
              key={slot.time}
              variant={selected ? 'primary' : 'outline'}
              size="sm"
              disabled={disabled}
              onClick={() => onSelect(slot.time)}
              className={cn(
                'text-xs sm:text-sm',
                selected && 'bg-blue-700 hover:bg-blue-800',
                disabled && 'opacity-40 cursor-not-allowed'
              )}
            >
              {slot.label}
            </Button>
          );
        })}
      </div>
      {selectedDate.getDay() === 0 && (
        <p className="text-sm text-slate-500">We are closed on Sundays. Please select another day.</p>
      )}
      {selectedDate.getDay() === 6 && (
        <p className="text-sm text-slate-500">We are closed on Saturdays. Please select another day.</p>
      )}
    </div>
  );
}
